import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { CopyAll, Download } from "@mui/icons-material";
import { useSnackBar } from "../FlowBuilder/Common/SnackBar/index.tsx";
import { useRecoilValue } from "recoil";
import { userInformationAtom } from "../../Services/_states/index.js";

interface BeautifyTextProps {
  text: any; // keep as any so we can accept raw objects for superuser
  title?: string;
}

/* ---------- helpers (unchanged) ---------- */
function extractFinalResponseText(input: any): string {
  if (!input) return "";

  // Case 1: direct string
  if (typeof input === "string") return input;

  if (typeof input === "object") {
    // Case 2: simple object → response is string
    if (typeof input?.response === "string") return input.response;

    // Case 3: nested structure → response.response (your new JSON type)
    if (typeof input?.response?.response === "string") return input.response.response;

    // Case 4: deeper structure → data.response.response
    if (typeof input?.data?.response?.response === "string") return input.data.response.response;

    // Case 5: some flows return → response.data.response.response
    if (typeof input?.response?.data?.response?.response === "string")
      return input.response.data.response.response;

    // Case 6: response.output_query only (no answer)
    if (typeof input?.response?.output_query === "string") return input.response.output_query;
  }

  return "";
}


function parseLineBoldParts(text: string) {
  const regex = /(\*\*|__)(.+?)\1/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.substring(lastIndex, match.index));
    parts.push(
      <Typography key={`bold-${match.index}`} component="span" sx={{ fontWeight: "bold", display: "inline" }}>
        {match[2]}
      </Typography>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.substring(lastIndex));
  return parts.length > 0 ? parts : text;
}

function tryParseJSON(text: string): object | null {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function isTable(lines: string[]) {
  if (lines.length < 2) return false;
  const header = lines[0];
  const separator = lines[1];
  return /^\s*\|.*\|\s*$/.test(header) && /^\s*\|?(\s*:?-+:?\s*\|)+\s*$/.test(separator);
}

function parseTable(lines: string[]) {
  return lines.map((line) =>
    line
      .trim()
      .replace(/^\||\|$/g, "")
      .split("|")
      .map((cell) => cell.trim())
  );
}

function maybeBoldProvidePrompt(line: string): React.ReactNode | null {
  const providePhrases = ["please provide your", "please provide", "kindly provide", "please select", "kindly select"];
  const lowerLine = line.toLowerCase();

  for (const phrase of providePhrases) {
    const index = lowerLine.indexOf(phrase);
    if (index !== -1) {
      const before = line.slice(0, index);
      const phraseLength = phrase.length;
      const after = line.slice(index + phraseLength).trim();

      return (
        <>
          <Typography component="span">{before}</Typography>
          <Typography component="span">{line.slice(index, index + phraseLength)} </Typography>
          <Typography component="span" sx={{ fontWeight: "bold" }}>
            {after}
          </Typography>
        </>
      );
    }
  }
  return null;
}

const URL_REGEX = /(https?:\/\/[^\s'"<>\\]+)/g;

function renderStringWithLinks(str: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = URL_REGEX.exec(str)) !== null) {
    if (match.index > lastIndex) nodes.push(str.slice(lastIndex, match.index));

    const raw = match[0];

    const trailingStripRegex = /(?:(?:\\[nrtfv])|%0A|%0D|%5C|%5c|%22|%27|["')\]\.,;:!?]+)+$/i;
    const cleaned = raw.replace(trailingStripRegex, "");

    const href = cleaned || raw;

    const removedSuffix = raw.slice(href.length);

    nodes.push(
      <a
        key={`url-${match.index}-${nodes.length}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#1a73e8", textDecoration: "underline" }}
      >
        {href}
      </a>
    );

    if (removedSuffix) nodes.push(removedSuffix);

    lastIndex = match.index + raw.length;
  }

  if (lastIndex < str.length) nodes.push(str.slice(lastIndex));
  return nodes.length > 0 ? nodes : [str];
}

function linkifyParts(input: string | React.ReactNode[] | React.ReactNode): React.ReactNode[] {
  if (typeof input === "string") {
    return renderStringWithLinks(input);
  }
  if (Array.isArray(input)) {
    return input.flatMap((part, idx) => {
      if (typeof part === "string") {
        return renderStringWithLinks(part).map((n, j) =>
          React.isValidElement(n) ? React.cloneElement(n as React.ReactElement, { key: `p-${idx}-${j}` }) : <React.Fragment key={`p-${idx}-${j}`}>{n}</React.Fragment>
        );
      }
      if (React.isValidElement(part)) {
        return React.cloneElement(part, { key: `node-${idx}` });
      }
      return <React.Fragment key={`frag-${idx}`}>{part}</React.Fragment>;
    });
  }
  return [React.isValidElement(input) ? React.cloneElement(input as React.ReactElement, { key: "single" }) : input];
}

/* ---------- NEW: inline markdown renderer (keeps everything as React nodes) ---------- */
function renderInlineMarkdownToReact(text: string): React.ReactNode[] {
  if (!text) return [];

  // Step 1: handle markdown links [label](url)
  const linkMdRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = linkMdRegex.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    nodes.push(
      <a key={`mdlink-${match.index}`} href={match[2]} target="_blank" rel="noopener noreferrer" style={{ color: "#1a73e8", textDecoration: "underline" }}>
        {match[1]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));

  // Now nodes is an array of strings and <a> nodes.
  // For each string part, we further parse bold, italic, inline code and raw URLs.
  const finalNodes: React.ReactNode[] = [];

  const italicRegex = /\*(?!\*)(.+?)\*(?!\*)/g; // single *italic*
  const codeRegex = /`([^`]+)`/g;

  function processStringPiece(piece: string, keyBase: string) {
    if (!piece) return [];

    // First handle inline code pieces
    const codeParts: React.ReactNode[] = [];
    let cLast = 0;
    let cmatch: RegExpExecArray | null;
    while ((cmatch = codeRegex.exec(piece)) !== null) {
      if (cmatch.index > cLast) codeParts.push(piece.slice(cLast, cmatch.index));
      codeParts.push(
        <Box component="code" key={`${keyBase}-code-${cmatch.index}`} sx={{ fontFamily: "monospace", bgcolor: "#f4f4f4", px: "4px", borderRadius: "4px" }}>
          {cmatch[1]}
        </Box>
      );
      cLast = cmatch.index + cmatch[0].length;
    }
    if (cLast < piece.length) codeParts.push(piece.slice(cLast));

    // For each codeParts element that is a string, handle bold and italic and raw links
    const resultParts: React.ReactNode[] = [];

    codeParts.forEach((part, pi) => {
      if (typeof part === "string") {
        // Bold first using existing helper (returns array or string)
        const boldParsed = parseLineBoldParts(part);
        const boldArray = Array.isArray(boldParsed) ? boldParsed : [boldParsed];

        boldArray.forEach((b, bi) => {
          if (typeof b === "string") {
            // Italic processing
            let iLast = 0;
            let imatch: RegExpExecArray | null;
            let localIndex = 0;
            while ((imatch = italicRegex.exec(b)) !== null) {
              if (imatch.index > iLast) {
                const before = b.slice(iLast, imatch.index);
                // linkify before
                const linkified = renderStringWithLinks(before);
                linkified.forEach((n, idx) => {
                  resultParts.push(React.isValidElement(n) ? React.cloneElement(n as React.ReactElement, { key: `${keyBase}-link-${pi}-${bi}-${localIndex}-${idx}` }) : <React.Fragment key={`${keyBase}-frag-${pi}-${bi}-${localIndex}-${idx}`}>{n}</React.Fragment>);
                });
              }
              resultParts.push(
                <Typography key={`${keyBase}-italic-${pi}-${bi}-${localIndex}`} component="span" sx={{ fontStyle: "italic", display: "inline" }}>
                  {imatch[1]}
                </Typography>
              );
              iLast = imatch.index + imatch[0].length;
              localIndex++;
            }
            if (iLast < b.length) {
              const rest = b.slice(iLast);
              const linkified = renderStringWithLinks(rest);
              linkified.forEach((n, idx) => {
                resultParts.push(React.isValidElement(n) ? React.cloneElement(n as React.ReactElement, { key: `${keyBase}-link-${pi}-${bi}-end-${idx}` }) : <React.Fragment key={`${keyBase}-frag-end-${pi}-${bi}-${idx}`}>{n}</React.Fragment>);
              });
            }
          } else if (React.isValidElement(b)) {
            resultParts.push(React.cloneElement(b as React.ReactElement, { key: `${keyBase}-boldNode-${pi}-${bi}` }));
          } else {
            resultParts.push(<React.Fragment key={`${keyBase}-other-${pi}-${bi}`}>{b}</React.Fragment>);
          }
        });
      } else {
        // it's a React node (like <code>)
        resultParts.push(part);
      }
    });

    return resultParts;
  }

  nodes.forEach((n, idx) => {
    if (typeof n === "string") {
      const processed = processStringPiece(n, `seg-${idx}`);
      processed.forEach((pn) => finalNodes.push(pn));
    } else if (React.isValidElement(n)) {
      finalNodes.push(React.cloneElement(n as React.ReactElement, { key: `mdlink-node-${idx}` }));
    } else {
      finalNodes.push(<React.Fragment key={`mdfrag-${idx}`}>{n}</React.Fragment>);
    }
  });

  return finalNodes.length > 0 ? finalNodes : [text];
}

/* ---------- NEW: JsonBlock component (keeps your copy button) ---------- */
function JsonBlock({ obj }: { obj: any }) {
  const jsonStr = typeof obj === "string" ? obj : JSON.stringify(obj, null, 2);
  const lines = jsonStr.split("\n");
  const { showSnackBar } = useSnackBar();

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(jsonStr)
      .then(() => showSnackBar("JSON copied to clipboard!", "success"))
      .catch(() => showSnackBar("Failed to copy JSON.", "error"));
  };

  return (
    <Box
      component="pre"
      sx={{
        backgroundColor: "#eee",
        color: "#333",
        p: 2,
        borderRadius: 1,
        overflowX: "auto",
        fontSize: "0.9rem",
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
        mb: 2,
        border: "1px solid #ccc",
        position: "relative",
      }}
    >
      <IconButton onClick={copyToClipboard} sx={{ position: "absolute", top: 8, right: 8 }} color="primary" size="small">
        <CopyAll fontSize="small" />
      </IconButton>

      {lines.map((line, idx) => {
        let style: React.CSSProperties = {};
        if (/^\s*"/.test(line)) style = { color: "#d14" }; // keys
        if (/:\s*".*"/.test(line)) style = { color: "#099" }; // string values
        if (/:\s*\d/.test(line)) style = { color: "#905" }; // numbers
        if (/:\s*(true|false|null)/.test(line)) style = { color: "#069" }; // booleans/null
        const rendered = linkifyParts(line);

        return (
          <span key={idx} style={style}>
            {rendered}
            {"\n"}
          </span>
        );
      })}
    </Box>
  );
}

/* ---------- MAIN COMPONENT ---------- */
export function BeautifyText({ text: rawInput, title }: BeautifyTextProps) {
  const loggedInUser = useRecoilValue(userInformationAtom);
  const isSuperuser = !!loggedInUser?.is_superuser;

  // Superuser: show FULL RAW JSON when possible, without extraction.
  // Non-superuser: keep existing extraction logic.
  const effective = isSuperuser ? rawInput : extractFinalResponseText(rawInput);

  // If superuser and the input is an object → directly render JSON and return early.
  if (isSuperuser && typeof effective === "object" && effective !== null) {
    return (
      <Box sx={{ mb: 0, bgcolor: "#eeeeee00", p: { xs: 1, sm: 1, md: 1 }, borderRadius: 1 }}>
        {title && (
          <Typography variant="h6" sx={{ mb: 1 }}>
            {title}
          </Typography>
        )}
        <JsonBlock obj={effective} />
      </Box>
    );
  }

  // If superuser and input is a JSON string → render as JSON early.
  if (isSuperuser && typeof effective === "string") {
    const parsed = tryParseJSON(effective);
    if (parsed) {
      return (
        <Box sx={{ mb: 0, bgcolor: "#eeeeee00", p: { xs: 1, sm: 1, md: 1 }, borderRadius: 1 }}>
          {title && (
            <Typography variant="h6" sx={{ mb: 1 }}>
              {title}
            </Typography>
          )}
          <JsonBlock obj={parsed} />
        </Box>
      );
    }
  }

  // ------ below: your existing beautify path for non-superusers (enhanced with markdown) ------
  const text = typeof effective === "string" ? effective : JSON.stringify(effective || "");
  const elements: React.ReactNode[] = [];
  const lines = text.split(/\r?\n/);

  let listItems: React.ReactNode[] = [];
  function flushList() {
    if (listItems.length > 0) {
      elements.push(
        <Box component="ul" sx={{ pl: { xs: 2, sm: 2, md: 4 }, mb: 2 }} key={`ul-${elements.length}`}>
          {listItems}
        </Box>
      );
      listItems = [];
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    const line = rawLine.trim();

    // HEADINGS support: lines starting with #, ##, ### etc.
    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      flushList();
      const level = headingMatch[1].length;
      const content = headingMatch[2];
      const node = (
        <Typography
          key={`h-${i}`}
          variant={level === 1 ? "h6" : level === 2 ? "subtitle1" : "subtitle2"}
          sx={{ fontWeight: "bold", mb: 1 }}
        >
          {renderInlineMarkdownToReact(content)}
        </Typography>
      );
      elements.push(node);
      continue;
    }

    // JSON block detection (unchanged)
    if (line.startsWith("{") || line.startsWith("[")) {
      let jsonBlockLines = [lines[i]];
      let j = i + 1;
      for (; j < lines.length; j++) {
        if (lines[j].trim() === "") break;
        jsonBlockLines.push(lines[j]);
      }
      const jsonBlockText = jsonBlockLines.join("\n");
      const parsedJSON = tryParseJSON(jsonBlockText);
      if (parsedJSON !== null) {
        flushList();
        elements.push(<JsonBlock key={`json-${i}`} obj={parsedJSON} />);
        i = j - 1;
        continue;
      }
    }

    // Markdown table detection (unchanged)
    if (i + 1 < lines.length) {
      const maybeTableLines = lines.slice(i, i + 3);
      if (isTable(maybeTableLines)) {
        flushList();
        const tableLines: string[] = [];
        let k = i;
        while (k < lines.length && lines[k].includes("|") && lines[k].trim() !== "") {
          tableLines.push(lines[k]);
          k++;
        }
        i = k - 1;

        const rows = parseTable(tableLines);

        elements.push(
          <Box key={`table-${elements.length}`}>
            <Tooltip title="Download Table as CSV">
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => {
                  const csvData = rows.map((row) => row.join(",")).join("\n");
                  const blob = new Blob([csvData], { type: "text/csv" });
                  const link = document.createElement("a");
                  link.href = URL.createObjectURL(blob);
                  link.download = "table.csv";
                  link.click();
                }}
                sx={{ mb: 2 }}
              >
                <Download /> Download CSV
              </Button>
            </Tooltip>
            <Table
              size="small"
              aria-label="markdown table"
              sx={{
                mb: 3,
                border: "1px solid #ccc",
                bgcolor: "#eee",
                borderRadius: 1,
              }}
            >
              <TableHead>
                <TableRow>
                  {rows[0].map((cell, idx) => (
                    <TableCell key={`head-${idx}`} sx={{ fontWeight: "bold", bgcolor: "#ddd" }}>
                      {cell}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice(2).map((row, ridx) => (
                  <TableRow key={`row-${ridx}`}>
                    {row.map((cell, cidx) => (
                      <TableCell key={`cell-${ridx}-${cidx}`}>{cell}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        );
        continue;
      }
    }

    // Bullet list detection (unchanged) - but render inline markdown inside bullets
    if (/^(\*|\-)\s+/.test(line)) {
      const content = line.replace(/^(\*|\-)\s+/, "");
      const parts = renderInlineMarkdownToReact(content);
      listItems.push(
        <li key={`li-${i}`} style={{ marginBottom: "4px" }}>
          <Typography component="span" variant="body1">
            {parts}
          </Typography>
        </li>
      );
      continue;
    }

    if (line === "") {
      flushList();
      elements.push(<Box key={`br-${i}`} sx={{ height: 8 }} />);
    } else {
      flushList();
      const provideFormatted = maybeBoldProvidePrompt(line);
      if (provideFormatted) {
        // If maybeBoldProvidePrompt returned a composed node, ensure inline markdown on textual children:
        elements.push(
          <Typography key={`p-${i}`} variant="body1" sx={{ whiteSpace: "pre-wrap", mb: 1 }} component="p">
            {React.Children.toArray((provideFormatted as React.ReactElement).props.children).map((child, idx) => {
              if (typeof child === "string") return renderInlineMarkdownToReact(child);
              if (React.isValidElement(child)) return React.cloneElement(child as React.ReactElement, { key: `pf-${i}-${idx}` });
              return child;
            })}
          </Typography>
        );
      } else {
        // Use inline markdown renderer for the paragraph line
        const parts = renderInlineMarkdownToReact(line);
        elements.push(
          <Typography key={`p-${i}`} variant="body1" sx={{ whiteSpace: "pre-wrap", mb: 1 }} component="p">
            {parts}
          </Typography>
        );
      }
    }
  }

  flushList();

  return (
    <Box sx={{ mb: 0, bgcolor: "#eeeeee00", p: { xs: 1, sm: 1, md: 1 }, borderRadius: 1 }}>
      {title && (
        <Typography variant="h6" sx={{ mb: 1 }}>
          {title}
        </Typography>
      )}
      <Box>{elements}</Box>
    </Box>
  );
}

export default BeautifyText;
